import type { LinkedSessionContext } from '@interactive-room';

import { buildCourseMaterialPath, buildCourseRubricPath } from '../constants/courseStudioRoutes';
import {
  materialIntentNavigationState,
  materialIntentRoute,
  type MaterialIntent,
  type MaterialIntentNavigationState,
} from '../constants/materialIntent';
import { buildStudioMaterialUrl } from '../constants/studioMaterialRoutes';
import {
  FLOW_PROFILE_TO_ARCHETYPE,
  type StudioCreateResourceType,
  type StudioFlowProfile,
} from '../constants/studioCreateConstants';
import type { CourseProfile } from '../types/courseProfile';
import { resolveCourseDisplayName } from '../constants/courseProfileUi';

export interface StudioCreateWizardResult {
  resourceType: StudioCreateResourceType;
  teacherPrompt: string;
  flowProfile: StudioFlowProfile;
  course?: CourseProfile | null;
  subjectId: string;
  /** Sesión planificada — prioridad sobre contexto sintético del curso. */
  linkedSessionContext?: LinkedSessionContext;
}

/** Estado al abrir rúbrica desde el wizard global. */
export interface RubricCreateNavigationState {
  teacherPrompt: string;
}

export type StudioCreateNavigationState =
  | MaterialIntentNavigationState
  | RubricCreateNavigationState;

function resourceTypeToMaterialIntent(resourceType: StudioCreateResourceType): MaterialIntent | null {
  if (resourceType === 'guide') return 'print';
  if (resourceType === 'presentation') return 'interactive';
  return null;
}

function enrichLinkedSessionContextFromWizard(
  base: LinkedSessionContext,
  teacherPrompt: string,
  flowProfile: StudioFlowProfile,
): LinkedSessionContext {
  const archetype = FLOW_PROFILE_TO_ARCHETYPE[flowProfile];
  const promptTitle = teacherPrompt.trim().replace(/\s+/g, ' ').slice(0, 64);
  const prevConfig = base.sessionConfig && typeof base.sessionConfig === 'object' ? base.sessionConfig : {};
  const { archetype: _ignored, ...restConfig } = prevConfig as Record<string, unknown>;

  return {
    ...base,
    ...(isWizardPlaceholderTitle(base.sessionTitle) && promptTitle
      ? { sessionTitle: promptTitle }
      : {}),
    // Secuencia libre (archetype null): no dejes sessionConfig vacío/`{}` o con archetype huérfano.
    ...(archetype
      ? { sessionConfig: { ...restConfig, archetype } }
      : { sessionConfig: undefined }),
    flowProfile,
  };
}

function isWizardPlaceholderTitle(title: string | undefined): boolean {
  const t = String(title || '')
    .trim()
    .toLowerCase();
  return !t || t === 'material nuevo' || t === 'material del curso';
}

export function buildLinkedSessionContextFromWizard(
  course: CourseProfile,
  teacherPrompt: string,
  flowProfile: StudioFlowProfile,
): LinkedSessionContext {
  const courseLabel = resolveCourseDisplayName(course);
  const archetype = FLOW_PROFILE_TO_ARCHETYPE[flowProfile];
  const promptTitle = teacherPrompt.trim().replace(/\s+/g, ' ').slice(0, 64);

  return {
    courseId: course.id,
    unitId: 'studio-create',
    sessionId: `wizard-${Date.now()}`,
    unitTitle: courseLabel || 'Curso',
    // Título desde el pedido docente — nunca "Material nuevo".
    sessionTitle: promptTitle || 'Material del curso',
    sessionObjective: promptTitle || 'Material del curso',
    sessionSummary: promptTitle || courseLabel || 'Material del curso',
    sessionConfig: archetype ? { archetype } : {},
    subjectTag: course.subjectTag,
    level: course.level,
    flowProfile,
  };
}

export function appendFlowProfileToPath(path: string, flowProfile: StudioFlowProfile): string {
  const [base, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  params.set('flowProfile', flowProfile);
  return `${base}?${params.toString()}`;
}

export function buildStudioCreateNavigation(
  result: StudioCreateWizardResult,
): { path: string; state?: StudioCreateNavigationState } {
  const { resourceType, teacherPrompt, flowProfile, course, subjectId, linkedSessionContext: sessionContext } =
    result;

  if (resourceType === 'rubric') {
    const rubricCourseId = sessionContext?.courseId ?? course?.id;
    if (!rubricCourseId) {
      return { path: '/studio' };
    }
    const trimmed = teacherPrompt.trim();
    return {
      path: buildCourseRubricPath(rubricCourseId, 'nueva'),
      ...(trimmed ? { state: { teacherPrompt: trimmed } satisfies RubricCreateNavigationState } : {}),
    };
  }

  const intent = resourceTypeToMaterialIntent(resourceType);
  if (!intent) {
    return { path: '/studio' };
  }

  const linkedSessionContext = sessionContext
    ? enrichLinkedSessionContextFromWizard(sessionContext, teacherPrompt, flowProfile)
    : course
      ? buildLinkedSessionContextFromWizard(course, teacherPrompt, flowProfile)
      : undefined;

  const effectiveCourseId = linkedSessionContext?.courseId ?? course?.id;

  const basePath = effectiveCourseId
    ? buildCourseMaterialPath(
        effectiveCourseId,
        materialIntentRoute(intent, subjectId, effectiveCourseId),
      )
    : buildStudioMaterialUrl({
        profile: intent === 'print' ? 'learning_guide' : 'interactive_class',
        subjectId,
      });

  const path = appendFlowProfileToPath(basePath, flowProfile);

  return {
    path,
    state: materialIntentNavigationState(intent, linkedSessionContext, {
      teacherPrompt: teacherPrompt.trim() || undefined,
      flowProfile,
    }),
  };
}
